import type {
  ConnectionHealth,
  ConnectionTestResult,
} from '@/lib/api/generated/model';
import { formatRelativeTime } from '@/shared/utils/time';

export type HealthTone = 'success' | 'warning' | 'destructive' | 'muted';

export interface HealthChipState {
  kind: 'checking' | 'working' | 'failing' | 'not-tested';
  label: 'Checking…' | 'Working' | 'Failing' | 'Not tested';
  tone: HealthTone;
  /** Shown as the title and in the accessible name: "Credentials rejected · checked 5m ago". */
  detail?: string;
}

/** The live test query, reduced to what the chip reads of it. */
export interface LiveHealth {
  status: 'pending' | 'success' | 'error';
  data?: ConnectionTestResult;
}

/** The service's words for the causes it records (spec 139). */
const HEALTH_CAUSE: Partial<Record<string, string>> = {
  credentials_rejected: 'Credentials rejected',
  access_denied: 'Access denied',
  test_failed: 'The connection test failed',
};

/**
 * One connection's health as one chip (spec 229 research R1): the live test
 * when it ran, the recorded health when it could not, and "Not tested" only
 * when there is neither. The table ran the live test and the list rows read
 * the record, so the two surfaces could disagree; one function over both
 * keeps them saying the same thing.
 *
 * A rejected credential is a warning, not a danger (brand FD-13): the person
 * can fix it here. Other recorded failures are destructive.
 */
export function healthState(
  live: LiveHealth,
  recorded: ConnectionHealth | null | undefined,
): HealthChipState {
  if (live.status === 'pending') {
    return { kind: 'checking', label: 'Checking…', tone: 'muted' };
  }
  if (live.status === 'success' && live.data) {
    if (live.data.ok) {
      return { kind: 'working', label: 'Working', tone: 'success' };
    }
    return {
      kind: 'failing',
      label: 'Failing',
      tone: 'destructive',
      detail: live.data.message,
    };
  }
  if (recorded?.status === 'failing') {
    const cause =
      (recorded.reason ? HEALTH_CAUSE[recorded.reason] : undefined) ??
      recorded.message ??
      'Failing';
    return {
      kind: 'failing',
      label: 'Failing',
      tone:
        recorded.reason === 'credentials_rejected' ? 'warning' : 'destructive',
      detail: `${cause} · checked ${formatRelativeTime(recorded.checked_at)}`,
    };
  }
  if (recorded?.status === 'ok') {
    return {
      kind: 'working',
      label: 'Working',
      tone: 'success',
      detail: `checked ${formatRelativeTime(recorded.checked_at)}`,
    };
  }
  return { kind: 'not-tested', label: 'Not tested', tone: 'muted' };
}
